import React, { useState, useEffect } from "react";
import ErrorStatus from "@pages/Project/components/Tabs/components/Anomaly/ErrorStatus";
import {
  AnomalyFinding,
  FileStatusPopupProps,
  statusPillColorMap,
  sectionIconMap,
  sectionTitleColorMap,
  SeverityLevel,
} from "@shared/interfaces/Anomaly";
import { fileService } from "@services/FileService";
import { useParams } from "react-router-dom";
import ExcelPreviewModal from "../../ExcelPreviewModal";
import { apiService } from "@services/ApiService";

const FileStatusPopup: React.FC<FileStatusPopupProps> = ({ data, onClose }) => {
  const [entries, setEntries] = useState<AnomalyFinding[]>([]);
  const [isXlsModalOpen, setIsXlsModalOpen] = useState<boolean>(false);
  const [excelFileType, setExcelFileType] = useState<string>("");
  const [excelBlob, setExcelBlob] = useState<Blob | null>(null);
  const [anamolyData, setAnomalyData] = useState<any>([]);
  const [loadingIndex, setLoadingIndex] = useState<string | null>(null);

  const { projectId } = useParams();

  useEffect(() => {
    setEntries(data.anomaly_data.findings || []);
  }, [data]);

  useEffect(() => {
    if (isXlsModalOpen) {
      openExcelFile(data.file.id);
    }
  }, [isXlsModalOpen]);

  const handleResolve = async (id: string) => {
    if (!projectId) return;
    try {
      const updatedEntry = await fileService.resolveAnomaly(
        projectId,
        file.id,
        id
      );
      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === id ? { ...entry, ...updatedEntry } : entry
        )
      );
    } catch (error) {
      console.error("Failed to ignore anomaly:", error);
    }
  };

  const handleIgnore = async (id: string) => {
    if (!projectId) return;
    try {
      const updatedEntry = await fileService.ignoreAnomaly(
        projectId,
        file.id,
        id
      );
      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === id ? { ...entry, ...updatedEntry } : entry
        )
      );
    } catch (error) {
      console.error("Failed to ignore anomaly:", error);
    }
  };

  const handleIgnoreAll = async () => {
    if (!projectId) return;
    try {
      await fileService.ignoreAllAnomalies(projectId, file.id);
      setEntries((prev) =>
        prev.map((entry) =>
          entry.resolution_status === "Pending"
            ? {
                ...entry,
                resolution_status: "Ignored",
              }
            : entry
        )
      );
    } catch (error) {
      console.error("Failed to ignore anomalies:", error);
    }
  };

  const { file } = data;

  const openExcelFile = async (fileId: string) => {
    try {
      const response = await apiService.get(
        `/projects/${projectId}/files/${fileId}`,
        {
          responseType: "blob",
        }
      );

      const file = new Blob([response?.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      setExcelBlob(file);
      // setIsXlsModalOpen(true);
    } catch (error) {
      console.error("Error loading XLSX file:", error);
    } finally {
      setLoadingIndex(null);
    }
  };

  const maxSeverity = file.max_anomalies_severity || "Success";
  const pendingIssues = entries.filter(
    (e) => e.resolution_status === "Pending"
  ).length;
  const allResolved = pendingIssues === 0 && file.anomaly_count !== 0;
  const headerColor =
    statusPillColorMap[
      allResolved ? "Success" : (maxSeverity as SeverityLevel)
    ];
  const pillText = allResolved ? "Resolved" : maxSeverity;
  const description = file.max_anomalies_severity
    ? `There ${
        pendingIssues === 1 ? "is" : "are"
      } ${pendingIssues} unresolved ${
        entries.length === 1 ? "issue" : "issues"
      }`
    : "No issues found";
  const sectionTitle = allResolved ? "No Required Actions" : "Required Actions";
  const sectionTitleColor =
    sectionTitleColorMap[
      allResolved ? "Success" : (maxSeverity as SeverityLevel)
    ];
  const sectionIcon =
    sectionIconMap[allResolved ? "Success" : (maxSeverity as SeverityLevel)];

  return (
    <div className="max-w-2xl w-full rounded-xl shadow-xl bg-white border border-gray-200 max-h-[90vh]  flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between p-5 border-b border-gray-200">
        <div>
          <h2 className="text-base 2xl:text-lg font-semibold text-gray-900">
            {file.filename}
          </h2>
          <p className={`text-xs 2xl:text-sm mt-1 ${sectionTitleColor}`}>
            {description}
          </p>
        </div>
        <span
          className={`text-xs 2xl:text-sm font-medium px-4 border py-0.5 pt-1 rounded-xl ${headerColor}`}
        >
          {pillText}
        </span>
      </div>

      {/* Body */}
      <div className="p-5 flex-1 overflow-y-auto overflow-x-hidden">
        <div
          className={`flex items-center gap-2 font-semibold mb-0 ${sectionTitleColor}`}
        >
          <img src={sectionIcon} alt="Status" className="w-5 h-5" />
          <span className="text-sm 2xl:text-base">{sectionTitle}</span>
        </div>

        {entries.length ? (
          <div className="px-4">
            {entries.map((entry, index) => (
              <div
                key={index}
                className="text-xs 2xl:text-sm text-gray-800 border-gray-200 w-full items-center"
              >
                <ErrorStatus
                  key={index}
                  entry={entry}
                  handleResolve={handleResolve}
                  handleIgnore={handleIgnore}
                  setAnomalyData={setAnomalyData}
                  setIsXlsModalOpen={setIsXlsModalOpen}
                  loadingIndex={loadingIndex}
                  setLoadingIndex={setLoadingIndex}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-600 pl-7">You're all set 🎉</div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-4 px-5 py-5 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="border text-sm 2xl:text-base border-blue-600 text-blue-600 px-4 py-2 rounded-md font-medium hover:bg-blue-50 transition w-1/2"
        >
          Close
        </button>
        <button
          type="button"
          onClick={handleIgnoreAll}
          disabled={pendingIssues == 0}
          className={`px-4 py-2 rounded-md text-sm 2xl:text-base font-medium transition w-1/2 ${
            pendingIssues > 0
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          Ignore All
        </button>
      </div>

      {isXlsModalOpen && excelBlob && (
        <ExcelPreviewModal
          excelFileType={excelFileType}
          excelBlob={excelBlob}
          setIsXlsModalOpen={setIsXlsModalOpen}
          setExcelBlob={setExcelBlob}
          anomalyData={anamolyData}
        />
      )}
    </div>
  );
};

export default FileStatusPopup;
